import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Avatar, Button, CheckIcon, XIcon } from 'fasoo-ui-component-next';
import { CommonProps } from 'fasoo-ui-component-next/dist/src/components/helpers/props';

import usePreventScrollStore from 'src/hooks/usePreventScroll';
import i18n from 'src/i18n';
import ModalPortal from 'src/shared/react-utils/ModalPortal';
import { AuthEntity } from 'src/store/auth/auth';

import './index.scss';

export interface LanguageSelectPopupProps extends CommonProps {
  open: boolean;
  onClose: () => void;
  currentLang: string;
  changeLang: (lang: string) => void;
}

const langList = ['ko', 'en'];
const touchPointCenter = 20;
const dafaultCurHeight = 202;

const LanguageSelectPopup: React.FC<LanguageSelectPopupProps> = ({
  open,
  onClose,
  currentLang,
  changeLang,
  classes,
}) => {
  const { t } = useTranslation('commons');
  const [isEnter, setIsEnter] = useState<boolean>(false);
  const { startPreventScroll, stopPreventScroll } = usePreventScrollStore();

  const dragRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  const handleChangeLang = (lang: string): void => {
    changeLang(lang);
    i18n.changeLanguage(lang);
    document.getElementById('root')?.setAttribute('lang', lang);
    document.documentElement.lang = lang;
  };

  const moveStart = useRef<boolean>(false);
  const startMove = useCallback((): void => {
    // console.log('startMove');
    startPreventScroll();
    moveStart.current = true;
  }, []);
  const popupMove = useCallback((curY: number): void => {
    if (moveStart.current && targetRef.current) {
      const winHeight = window.innerHeight;
      targetRef.current.style.height = `${winHeight - curY + touchPointCenter}px`;
    }
  }, []);
  const endMove = useCallback((checkHeight: number): void => {
    // console.log('endMove');
    if (moveStart.current && checkHeight < 60) {
      onClose();
      moveStart.current = false;
    } else if (moveStart) {
      if (targetRef.current) {
        targetRef.current.style.height = `${dafaultCurHeight}px`;
      }
      moveStart.current = false;
    }
  }, []);

  // 터치 드래그
  const handleTouchStart = useCallback((event: TouchEvent): void => {
    // console.log('touch start');
    startMove();
  }, []);
  const handleTouchMove = useCallback((event: TouchEvent): void => {
    popupMove(event.touches[0].clientY);
    event.preventDefault();
    event.stopPropagation();
  }, []);
  const handleTouchEnd = useCallback((event: TouchEvent): void => {
    // console.log('touch end');
    const winHeight = window.innerHeight;
    endMove(winHeight - event.changedTouches[0].clientY + touchPointCenter);
  }, []);

  // 마우스 드래그
  const handleMouseDown = useCallback((event: MouseEvent): void => {
    // console.log('mouse down');
    startMove();
  }, []);
  const handleMouseMove = useCallback((event: MouseEvent): void => {
    popupMove(event.clientY);
    event.preventDefault();
    event.stopPropagation();
  }, []);
  const handleMouseUp = useCallback((event: MouseEvent): void => {
    // console.log('mouse up');
    const winHeight = window.innerHeight;
    endMove(winHeight - event.clientY + touchPointCenter);
  }, []);

  useEffect(() => {
    if (targetRef.current) {
      // console.log('height default');
      targetRef.current.style.height = `${dafaultCurHeight}px`;
    }
    if (dragRef.current) {
      dragRef.current.addEventListener('touchstart', handleTouchStart);
      dragRef.current.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      dragRef.current.addEventListener('mousedown', handleMouseDown);
      dragRef.current.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      if (dragRef.current) {
        dragRef.current.removeEventListener('touchstart', handleTouchStart);
        dragRef.current.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        dragRef.current.removeEventListener('mousedown', handleMouseDown);
        dragRef.current.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }
    };
  }, [dragRef]);

  useEffect(() => {
    if (isEnter === false) {
      startPreventScroll();
      setIsEnter(true);
    }
    return () => {
      stopPreventScroll();
    };
  }, []);

  return (
    <ModalPortal>
      <div
        onClick={onClose}
        className={classNames('language-select-popup-root', { on: open }, classes)}
        onMouseMove={(event) => {
          // event.preventDefault();
          event.stopPropagation();
        }}
        onTouchMove={(event) => {
          // event.preventDefault();
          event.stopPropagation();
        }}
      />
      <div
        style={{ maxHeight: `202px` }}
        className={classNames('language-select-popup', classes)}
        ref={targetRef}
      >
        <div
          // onTouchStart={(event) => handleTouchStart(event)}
          // onTouchMove={(event) => handleTouchMove(event)}
          // onTouchEnd={(event) => handleTouchEnd(event)}
          // onMouseDown={(event) => handleMouseDown(event)}
          // onMouseMove={(event) => handleMouseMove(event)}
          // onMouseUp={(event) => handleMouseUp(event)}
          ref={dragRef}
          className="scroll-item-container"
        >
          <div className="scroll-item" />
        </div>
        <div className="language-select-popup-title">{t('commons:langSetting')}</div>
        <div className="language-item-container">
          {langList.map((langItem) => (
            <div
              key={langItem}
              className={classNames('lang-item', { selected: currentLang === langItem })}
              onClick={() => handleChangeLang(langItem)}
            >
              <div>{t(`commons:lang.${langItem}`)}</div>
              {currentLang === langItem && <CheckIcon />}
            </div>
          ))}
        </div>
      </div>
    </ModalPortal>
  );
};

export default LanguageSelectPopup;
